import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Building2, User } from 'lucide-react';
import { authService } from '../../services/authService';

const OrgSwitcher = ({ user, collapsed }) => {
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!user?.organizations || user.organizations.length <= 1) {
    return null;
  }

  const currentOrg = user.primaryOrganization || user.organization;
  const currentOrgId = currentOrg?._id || currentOrg;
  const currentOrgName = currentOrg?.name || 'Organization';

  const handleSwitch = async (orgId) => {
    if (orgId === currentOrgId || switching) return;
    setSwitching(true);
    try {
      await authService.switchOrg(orgId);
      window.location.reload();
    } catch (err) {
      console.error('Failed to switch organization:', err);
      setSwitching(false);
    }
  };

  const getOrgTypeBadge = (org) => {
    const isIndividual = org.type === 'individual';
    const label = isIndividual ? 'Individual' : org.name;
    return (
      <span
        className={`text-[10px] px-1.5 py-0.5 rounded-md font-medium leading-tight ${
          isIndividual
            ? 'bg-neutral-100 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400'
            : 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
        }`}
      >
        {isIndividual ? 'Individual' : 'Org'}
      </span>
    );
  };

  if (collapsed) {
    return (
      <div className="px-3 py-2" ref={dropdownRef}>
        <button
          onClick={() => setOpen(!open)}
          className="w-full flex items-center justify-center p-2 rounded-xl text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
          title={currentOrgName}
        >
          <Building2 size={18} />
        </button>
        {open && (
          <div className="absolute left-16 top-auto mt-[-36px] z-50 w-56 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl shadow-lg py-1">
            {user.organizations.map((org) => {
              const orgId = org._id || org;
              const orgName = org.name || 'Organization';
              const isActive = orgId === currentOrgId;
              return (
                <button
                  key={orgId}
                  onClick={() => handleSwitch(orgId)}
                  disabled={switching}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${
                    isActive
                      ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                      : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700'
                  } ${switching ? 'opacity-50 cursor-wait' : ''}`}
                >
                  <span className="truncate flex-1">{orgName}</span>
                  {getOrgTypeBadge(org)}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="px-3 py-2 relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
      >
        <Building2 size={16} className="flex-shrink-0 text-neutral-400 dark:text-neutral-500" />
        <span className="truncate flex-1 text-left font-medium">{currentOrgName}</span>
        <ChevronDown
          size={14}
          className={`flex-shrink-0 text-neutral-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="absolute left-3 right-3 mt-1 z-50 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl shadow-lg py-1 overflow-hidden">
          {user.organizations.map((org) => {
            const orgId = org._id || org;
            const orgName = org.name || 'Organization';
            const isActive = orgId === currentOrgId;
            return (
              <button
                key={orgId}
                onClick={() => handleSwitch(orgId)}
                disabled={switching}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${
                  isActive
                    ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                    : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700'
                } ${switching ? 'opacity-50 cursor-wait' : ''}`}
              >
                <span className="truncate flex-1">{orgName}</span>
                {getOrgTypeBadge(org)}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OrgSwitcher;
